import { SUBJECT_BADGES, subjectBadgeStatus } from "./badges.js";
import { getLevel } from "./progression.js";
import { coursesForGrade, courseLabelFor, courseIconFor } from "../data/courses.js";

/**
 * Collapses game `state` into the plain shape the Achievements panel
 * renders (ui/achievementsPanel.js). Pure — no DOM, no `state` mutation —
 * so it's unit-tested directly in tests/achievements.test.js. Every
 * number is derived on demand; the panel owns no state of its own.
 * @param {object} state
 */
export function summarizeAchievements(state) {
  const totalAnswered = state.solvedCount + state.wrongCount;
  const accuracyPct = totalAnswered === 0
    ? null
    : Math.round((state.solvedCount / totalAnswered) * 100);

  // One row per subject the player has actually attempted, busiest first.
  // subjectSolveCounts/subjectWrongCounts are seeded for every subject in
  // gameState.js, so the keys are stable; we just skip the untouched ones.
  const bySubject = Object.keys(state.subjectSolveCounts)
    .map((id) => {
      const correct = state.subjectSolveCounts[id] ?? 0;
      const wrong = state.subjectWrongCounts[id] ?? 0;
      const answered = correct + wrong;
      return {
        id,
        label: courseLabelFor(id),
        icon: courseIconFor(id),
        correct,
        answered,
        accuracyPct: answered === 0 ? null : Math.round((correct / answered) * 100)
      };
    })
    .filter((row) => row.answered > 0)
    .sort((a, b) => b.answered - a.answered || a.label.localeCompare(b.label));

  // One badge row per subject worth showing: everything offered at the
  // current grade, plus any other subject the save has real progress in
  // (covers a grade change, and the legacy `food` pool — see badges.js).
  // Each row carries its Bronze/Silver/Gold levels (systems/badges.js).
  const offered = new Set(coursesForGrade(state.grade).map((c) => c.id));
  const badges = Object.keys(SUBJECT_BADGES)
    .map((id) => ({ id, status: subjectBadgeStatus(state.badges, id, state.subjectSolveCounts[id] ?? 0) }))
    .filter(({ id, status }) => offered.has(id) || status.current || status.solved > 0)
    .map(({ id, status }) => ({
      id,
      label: status.label,
      icon: courseIconFor(id),
      earned: Boolean(status.current),
      earnedCount: status.tiers.filter((t) => t.earned).length,
      current: status.current,
      next: status.next,
      tiers: status.tiers,
      progress: status.progress,
      threshold: status.threshold,
      pct: status.pct
    }))
    .sort((a, b) => b.earnedCount - a.earnedCount || b.pct - a.pct);

  const level = getLevel(state.level);

  return {
    level: { number: state.level + 1, name: level.name },
    speciesCollected: state.inventory.length,
    // Counted in medals: each subject row offers three.
    badgesEarned: badges.reduce((n, b) => n + b.earnedCount, 0),
    badgesTotal: badges.reduce((n, b) => n + b.tiers.length, 0),
    questions: {
      answered: totalAnswered,
      correct: state.solvedCount,
      accuracyPct
    },
    bySubject,
    badges
  };
}
